import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { BuildingOfficeIcon, MagnifyingGlassIcon, ChevronDownIcon, ChevronRightIcon, EllipsisVerticalIcon, RectangleGroupIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../contexts/PageTitleContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import apiService from '../services/api';

type SortField = 'name' | 'status' | 'created_at';
type SortOrder = 'asc' | 'desc';

interface Company {
  id: string;
  name: string;
  domain: string;
  description: string;
  website: string;
  logo_url: string;
  app_version: string;
  contact_person: string;
  contact_person_email: string;
  contact_email: string;
  address: string;
  is_active: boolean;
  total_licenses: number;
  available_licenses: number;
  managed_by?: string | null;
  created_at: string;
  updated_at: string;
}

const Companies: React.FC = () => {
  const { t } = useTranslation('companies');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const { setHasUnsavedChanges, setOnSave } = useUnsavedChanges();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [groupByManagedBy, setGroupByManagedBy] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set()); // Start collapsed (empty set)
  const [openCompanyMenu, setOpenCompanyMenu] = useState<string | null>(null);
  const [ownCompany, setOwnCompany] = useState<Company | null>(null);
  const [ownCompanyLoading, setOwnCompanyLoading] = useState(false);

  // Check view mode from localStorage and listen for changes
  const [viewMode, setViewMode] = useState<'user' | 'admin' | 'sysadmin'>(() => {
    try {
      const saved = localStorage.getItem('view_mode');
      if (saved) {
        const mode = saved as 'user' | 'admin' | 'sysadmin';
        if (user?.role === 'sysadmin') {
          return mode;
        } else if (user?.role === 'admin') {
          return mode === 'sysadmin' ? 'admin' : mode;
        }
        return 'user';
      }
      if (user?.role === 'admin' || user?.role === 'sysadmin') {
        return 'admin';
      }
      return 'user';
    } catch {
      return 'user';
    }
  });

  // Listen for view mode changes
  useEffect(() => {
    const handler = (event: CustomEvent) => {
      // Only handle view-mode-changed event (ignore administration-view-changed legacy event)
      if (event.type !== 'view-mode-changed') {
        return;
      }
      
      const newMode = event.detail?.viewMode;
      
      if (newMode && newMode !== viewMode) {
        // Use the event detail directly - it should have the latest value
        setViewMode(newMode as 'user' | 'admin' | 'sysadmin');
      }
    };
    window.addEventListener('view-mode-changed', handler as EventListener);
    // Don't listen to administration-view-changed - it's legacy and doesn't have viewMode
    return () => {
      window.removeEventListener('view-mode-changed', handler as EventListener);
    };
  }, [viewMode]); // Add viewMode to deps to compare with new value

  // Set page title and clear unsaved changes state
  useEffect(() => {
    setPageTitle(t('title'), t('subtitle'));
    setHasUnsavedChanges(false);
    setOnSave(null);
    
    // Clear any persisted company form tabs when loading companies list
    // This ensures we start fresh when navigating to a company edit page
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('companyForm_activeTab_')) {
        localStorage.removeItem(key);
      }
    });
  }, [setPageTitle, setHasUnsavedChanges, setOnSave, t]);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // 🔒 Auth via httpOnly cookies - no need for manual token
      const response = await apiService.get('/companies');
      const allCompanies = response.data.companies || [];
      // Force state update by creating a new array reference
      setCompanies([...allCompanies]);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchOwnCompany = useCallback(async () => {
    if (!user?.company_id) return;
    try {
      setOwnCompanyLoading(true);
      const response = await apiService.get(`/companies/${user.company_id}`);
      setOwnCompany(response.data);
    } catch (err) {
      console.error('Failed to fetch own company:', err);
    } finally {
      setOwnCompanyLoading(false);
    }
  }, [user?.company_id]);

  // Fetch data based on mode and user role
  useEffect(() => {
    if (!user) return;
    
    // Reset loading state when view mode changes
    setLoading(true);
    
    // Both sysadmin and company admin can access
    if (user.role === 'sysadmin' || user.role === 'admin') {
      // Reset state when switching modes - clear everything first
      setError(null);
      setOwnCompany(null);
      setCompanies([]); // Clear companies list immediately
      
      // For sysadmins, fetch based on view mode
      if (user.role === 'sysadmin') {
        if (viewMode === 'user') {
          // In user mode for sysadmins, fetch own company details
          fetchOwnCompany();
        } else {
          // In admin or sysadmin mode, fetch all companies
          fetchCompanies();
        }
      } else if (user.role === 'admin') {
        // For company admins, fetch based on view mode
        if (viewMode === 'user') {
          // In user mode for company admins, fetch own company details
          fetchOwnCompany();
        } else {
          // In admin mode, fetch all companies (they can only see their managed companies)
          fetchCompanies();
        }
      }
    }
    // Note: fetchCompanies and fetchOwnCompany are stable (useCallback with no deps)
    // so we don't need them in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, viewMode]);

  const handleCompanyClick = (company: Company) => {
    navigate(`/clients/${company.id}`);
  };

  // Filter companies by search term and view mode
  // - SysAdmin mode: show all companies (system-wide)
  // - Admin mode: for sysadmins, only show companies managed by their company
  // - User mode: don't filter (will show own company details instead)
  const filteredCompanies = useMemo(() => {
    let filtered = companies;
    
    // For sysadmins in admin mode: only show companies managed by their company
    // For sysadmins in sysadmin mode: show all companies (no filtering)
    if (user?.role === 'sysadmin' && viewMode === 'admin' && user?.company_id) {
      filtered = companies.filter(company => 
        company.managed_by === user.company_id
      );
    }
    // For sysadmins in sysadmin mode (viewMode === 'sysadmin'): show all companies (no filtering)
    // For sysadmins in user mode: don't filter (will show own company details)
    // For company admins (role === 'admin'): show all companies (they only see their managed ones anyway)
    
    // Apply search filter
    if (!searchTerm.trim()) return filtered;
    const term = searchTerm.toLowerCase();
    return filtered.filter(company => 
      company.name.toLowerCase().includes(term) ||
      (company.domain && company.domain.toLowerCase().includes(term))
    );
  }, [companies, searchTerm, user?.role, user?.company_id, viewMode]);

  // Sort companies
  const sortedCompanies = useMemo(() => {
    const sorted = [...filteredCompanies].sort((a, b) => {
      let aVal: any = '';
      let bVal: any = '';

      switch (sortField) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'status':
          aVal = a.is_active ? 1 : 0;
          bVal = b.is_active ? 1 : 0;
          break;
        case 'created_at':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredCompanies, sortField, sortOrder]);

  // Group companies by managed_by
  const groupedCompanies = useMemo(() => {
    if (!groupByManagedBy) {
      return { ungrouped: sortedCompanies };
    }

    const groups: Record<string, { manager: Company | null; companies: Company[] }> = {};
    
    sortedCompanies.forEach(company => {
      if (company.managed_by) {
        // Find the managing company
        const manager = companies.find(c => c.id === company.managed_by);
        if (!groups[company.managed_by]) {
          groups[company.managed_by] = { manager: manager || null, companies: [] };
        }
        groups[company.managed_by].companies.push(company);
      } else {
        // Companies without a manager go into ungrouped
        if (!groups.ungrouped) {
          groups.ungrouped = { manager: null, companies: [] };
        }
        groups.ungrouped.companies.push(company);
      }
    });

    return groups;
  }, [sortedCompanies, groupByManagedBy, companies]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openCompanyMenu) {
        setOpenCompanyMenu(null);
      }
    };
    if (openCompanyMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openCompanyMenu]);

  // Check if user is sysadmin or company admin
  if (!user || (user.role !== 'sysadmin' && user.role !== 'admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('accessDenied.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('accessDenied.description')}
          </p>
        </div>
      </div>
    );
  }

  const isSysadmin = user?.role === 'sysadmin';
  const isAdmin = user?.role === 'admin';

  // In user mode for sysadmins and company admins, show own company details
  if ((isSysadmin || isAdmin) && viewMode === 'user') {
    if (ownCompanyLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-governate-500"></div>
        </div>
      );
    }

    if (!ownCompany) {
      return (
        <div className="p-2 sm:p-4 md:p-6">
          <div className="card text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('noCompany.title')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('noCompany.description')}</p>
          </div>
        </div>
      );
    }

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    return (
      <div className="p-2 sm:p-4 md:p-6">
        <div className="card p-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <BuildingOfficeIcon className="w-5 h-5 mr-2" />
              {t('companyInfo.title')}
            </h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('companyInfo.companyName')}</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{ownCompany.name || t('na')}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('companyInfo.domain')}</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{ownCompany.domain || t('na')}</dd>
              </div>
              {ownCompany.description && (
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('companyInfo.description')}</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{ownCompany.description}</dd>
                </div>
              )}
              {ownCompany.website && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('companyInfo.website')}</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    <a href={ownCompany.website} target="_blank" rel="noopener noreferrer" className="text-governate-600 hover:text-governate-500">
                      {ownCompany.website}
                    </a>
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('companyInfo.appVersion')}</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                    {ownCompany.app_version || t('latest')}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('companyInfo.status')}</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    ownCompany.is_active ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                  }`}>
                    {ownCompany.is_active ? t('companyInfo.active') : t('companyInfo.inactive')}
                  </span>
                </dd>
              </div>
              {ownCompany.total_licenses !== undefined && (
                <>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('companyInfo.totalLicenses')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{ownCompany.total_licenses}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('companyInfo.availableLicenses')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{ownCompany.available_licenses}</dd>
                  </div>
                </>
              )}
              {ownCompany.created_at && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('companyInfo.created')}</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(ownCompany.created_at)}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-governate-500"></div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 md:p-6">
      {/* Error Message */}
      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-governate-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Sorting Controls */}
      <div className="bg-white dark:bg-black rounded-lg shadow mb-4 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:inline">{t('sortBy')}</span>
            {(['name', 'status', 'created_at'] as SortField[]).map((field) => (
              <button
                key={field}
                onClick={() => handleSort(field)}
                className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
                  sortField === field
                    ? 'bg-governate-100 text-governate-700 dark:bg-governate-900/30 dark:text-governate-300 font-medium'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center gap-1">
                  <span className={field === 'created_at' ? '' : 'capitalize'}>
                    {field === 'created_at' ? t('sortFields.registration') : t(`sortFields.${field}`)}
                  </span>
                  {sortField === field && (
                    <span className="text-governate-600 dark:text-governate-400">
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </button>
            ))}
            {isSysadmin && viewMode === 'sysadmin' && (
              <button
                onClick={() => setGroupByManagedBy(!groupByManagedBy)}
                className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
                  groupByManagedBy
                    ? 'bg-governate-100 text-governate-700 dark:bg-governate-900/30 dark:text-governate-300 font-medium'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                title={t('groupByManager')}
              >
                <div className="flex items-center gap-1.5">
                  <RectangleGroupIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('groupByManager')}</span>
                </div>
              </button>
            )}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t('organisationsCount', { count: sortedCompanies.length })}
          </div>
        </div>
      </div>

      {/* Companies List */}
      <div className="bg-white dark:bg-black rounded-lg shadow">
        {companies.length === 0 ? (
          <div className="px-6 py-12 text-center bg-white dark:bg-black">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">{t('empty.title')}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {isSysadmin ? t('empty.descriptionSysadmin') : t('empty.descriptionAdmin')}
            </p>
          </div>
        ) : sortedCompanies.length === 0 ? (
          <div className="px-6 py-12 text-center bg-white dark:bg-black">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">{t('emptySearch.title')}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {isSysadmin ? t('emptySearch.descriptionSysadmin') : t('emptySearch.descriptionAdmin')}
            </p>
          </div>
        ) : groupByManagedBy && isSysadmin && viewMode === 'sysadmin' ? (
          <div className="space-y-0">
            {Object.entries(groupedCompanies).map(([groupId, group]) => {
              const isExpanded = expandedGroups.has(groupId);
              const isUngrouped = groupId === 'ungrouped';
              
              return (
                <div key={groupId} className="border-b border-gray-200 dark:border-gray-800">
                  {/* Group Header (Managing Company) */}
                  {!isUngrouped && group.manager && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroup(groupId);
                      }}
                      className="bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors px-4 py-3 cursor-pointer flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {isExpanded ? (
                          <ChevronDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                        <div className="flex-shrink-0 h-8 w-8">
                          {group.manager.logo_url ? (
                            <img
                              src={group.manager.logo_url}
                              alt={`${group.manager.name} logo`}
                              className="h-8 w-8 object-contain rounded"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const icon = target.nextElementSibling as HTMLElement;
                                if (icon) icon.style.display = 'block';
                              }}
                            />
                          ) : null}
                          <BuildingOfficeIcon 
                            className="h-8 w-8 text-gray-400 p-1" 
                            style={{ display: group.manager.logo_url ? 'none' : 'block' }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {group.manager.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {t('managedOrganisationsCount', { count: group.companies.length })}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className={`w-2.5 h-2.5 rounded-full ${group.manager.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                      </div>
                    </div>
                  )}
                  
                  {/* Ungrouped Header */}
                  {isUngrouped && group.companies.length > 0 && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroup(groupId);
                      }}
                      className="bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors px-4 py-3 cursor-pointer flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {isExpanded ? (
                          <ChevronDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {t('selfManagedOrganisations')}
                        </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {t('organisationCount', { count: group.companies.length })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Grouped Companies */}
                  {isExpanded && (
                    <div className="space-y-0">
                      {group.companies.map((company: Company) => (
                        <div
                          key={company.id}
                          onClick={() => handleCompanyClick(company)}
                          className="border-l-4 border-l-transparent bg-white dark:bg-black hover:bg-gray-50 dark:hover:bg-gray-900 hover:border-l-governate-500 transition-all cursor-pointer px-4 py-3 pl-8 border-b border-gray-200 dark:border-gray-800"
                        >
                          {/* Row 1: Name | Status Dot | Created At */}
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex-shrink-0 h-10 w-10">
                                {company.logo_url ? (
                                  <img
                                    src={company.logo_url}
                                    alt={`${company.name} logo`}
                                    className="h-10 w-10 object-contain rounded"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                      const icon = target.nextElementSibling as HTMLElement;
                                      if (icon) icon.style.display = 'block';
                                    }}
                                  />
                                ) : null}
                                <BuildingOfficeIcon 
                                  className="h-10 w-10 text-gray-400 p-1" 
                                  style={{ display: company.logo_url ? 'none' : 'block' }}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {company.name}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              {company.created_at && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                  {(() => {
                                    const date = new Date(company.created_at);
                                    const year = date.getFullYear();
                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                    const day = String(date.getDate()).padStart(2, '0');
                                    const hours = String(date.getHours()).padStart(2, '0');
                                    const minutes = String(date.getMinutes()).padStart(2, '0');
                                    const seconds = String(date.getSeconds()).padStart(2, '0');
                                    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                                  })()}
                                </div>
                              )}
                              <div className={`w-2.5 h-2.5 rounded-full ${company.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                              
                              {/* Three dots menu */}
                              <div className="relative">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenCompanyMenu(openCompanyMenu === company.id ? null : company.id);
                                  }}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                  title={t('moreOptions')}
                                >
                                  <EllipsisVerticalIcon className="w-5 h-5" />
                                </button>
                                
                                {/* Dropdown Menu */}
                                {openCompanyMenu === company.id && (
                                  <div 
                                    className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="py-1">
                                      {/* Status */}
                                      <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('menu.status')}</div>
                                        <div className="flex items-center gap-2 mt-1">
                                          <div className={`w-2 h-2 rounded-full ${company.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                                          <span className="text-sm text-gray-900 dark:text-gray-100">
                                            {company.is_active ? t('menu.active') : t('menu.inactive')}
                                          </span>
                                        </div>
                                      </div>
                                      
                                      {/* Licenses */}
                                      {company.total_licenses > 0 && (
                                        <>
                                          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                            <div className="text-xs text-gray-500 dark:text-gray-400">{t('menu.totalLicenses')}</div>
                                            <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">{company.total_licenses}</div>
                                          </div>
                                          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                            <div className="text-xs text-gray-500 dark:text-gray-400">{t('menu.licensesUsed')}</div>
                                            <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                                              {company.total_licenses - company.available_licenses} / {company.total_licenses}
                                            </div>
                                          </div>
                                          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                            <div className="text-xs text-gray-500 dark:text-gray-400">{t('menu.licensesAvailable')}</div>
                                            <div className={`text-sm font-medium ${
                                              company.available_licenses === 0
                                                ? 'text-red-600 dark:text-red-400'
                                                : company.available_licenses <= company.total_licenses * 0.1
                                                ? 'text-yellow-600 dark:text-yellow-400'
                                                : 'text-green-600 dark:text-green-400'
                                            }`}>
                                              {company.available_licenses}
                                            </div>
                                          </div>
                                          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('menu.licenseUsage')}</div>
                                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                              <div 
                                                className={`h-2 rounded-full ${
                                                  (company.total_licenses - company.available_licenses) / company.total_licenses >= 0.9
                                                    ? 'bg-red-500'
                                                    : (company.total_licenses - company.available_licenses) / company.total_licenses >= 0.7
                                                    ? 'bg-yellow-500'
                                                    : 'bg-green-500'
                                                }`}
                                                style={{ 
                                                  width: `${Math.min(100, ((company.total_licenses - company.available_licenses) / company.total_licenses) * 100)}%` 
                                                }}
                                              />
                                            </div>
                                          </div>
                                        </>
                                      )}
                                      
                                      {/* Created At */}
                                      {company.created_at && (
                                        <div className="px-4 py-2">
                                          <div className="text-xs text-gray-500 dark:text-gray-400">{t('menu.created')}</div>
                                          <div className="text-sm text-gray-900 dark:text-gray-100">
                                            {(() => {
                                              const date = new Date(company.created_at);
                                              const year = date.getFullYear();
                                              const month = String(date.getMonth() + 1).padStart(2, '0');
                                              const day = String(date.getDate()).padStart(2, '0');
                                              const hours = String(date.getHours()).padStart(2, '0');
                                              const minutes = String(date.getMinutes()).padStart(2, '0');
                                              const seconds = String(date.getSeconds()).padStart(2, '0');
                                              return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                                            })()}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-0">
            {sortedCompanies.map((company) => (
              <div
                key={company.id}
                onClick={() => handleCompanyClick(company)}
                className="border-l-4 border-l-transparent bg-white dark:bg-black hover:bg-gray-50 dark:hover:bg-gray-900 hover:border-l-governate-500 transition-all cursor-pointer px-4 py-3 border-b border-gray-200 dark:border-gray-800"
              >
                {/* Row 1: Name | Status Dot | Created At */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 h-10 w-10">
                      {company.logo_url ? (
                        <img
                          src={company.logo_url}
                          alt={`${company.name} logo`}
                          className="h-10 w-10 object-contain rounded"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const icon = target.nextElementSibling as HTMLElement;
                            if (icon) icon.style.display = 'block';
                          }}
                        />
                      ) : null}
                      <BuildingOfficeIcon 
                        className="h-10 w-10 text-gray-400 p-1" 
                        style={{ display: company.logo_url ? 'none' : 'block' }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {company.name}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {company.created_at && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {(() => {
                          const date = new Date(company.created_at);
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          const hours = String(date.getHours()).padStart(2, '0');
                          const minutes = String(date.getMinutes()).padStart(2, '0');
                          const seconds = String(date.getSeconds()).padStart(2, '0');
                          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                        })()}
                      </div>
                    )}
                    <div className={`w-2.5 h-2.5 rounded-full ${company.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                    
                    {/* Three dots menu */}
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenCompanyMenu(openCompanyMenu === company.id ? null : company.id);
                        }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
title={t('moreOptions')}
                        >
                          <EllipsisVerticalIcon className="w-5 h-5" />
                        </button>
                        
                        {/* Dropdown Menu */}
                        {openCompanyMenu === company.id && (
                          <div 
                            className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="py-1">
                              {/* Status */}
                              <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                <div className="text-xs text-gray-500 dark:text-gray-400">{t('menu.status')}</div>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className={`w-2 h-2 rounded-full ${company.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                                  <span className="text-sm text-gray-900 dark:text-gray-100">
                                    {company.is_active ? t('menu.active') : t('menu.inactive')}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Licenses */}
                              {company.total_licenses > 0 && (
                                <>
                                  <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{t('menu.totalLicenses')}</div>
                                    <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">{company.total_licenses}</div>
                                  </div>
                                  <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{t('menu.licensesUsed')}</div>
                                    <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                                      {company.total_licenses - company.available_licenses} / {company.total_licenses}
                                    </div>
                                  </div>
                                  <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{t('menu.licensesAvailable')}</div>
                                  <div className={`text-sm font-medium ${
                                    company.available_licenses === 0
                                      ? 'text-red-600 dark:text-red-400'
                                      : company.available_licenses <= company.total_licenses * 0.1
                                      ? 'text-yellow-600 dark:text-yellow-400'
                                      : 'text-green-600 dark:text-green-400'
                                  }`}>
                                    {company.available_licenses}
                                  </div>
                                </div>
                                <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('menu.licenseUsage')}</div>
                                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full ${
                                        (company.total_licenses - company.available_licenses) / company.total_licenses >= 0.9
                                          ? 'bg-red-500'
                                          : (company.total_licenses - company.available_licenses) / company.total_licenses >= 0.7
                                          ? 'bg-yellow-500'
                                          : 'bg-green-500'
                                      }`}
                                      style={{ 
                                        width: `${Math.min(100, ((company.total_licenses - company.available_licenses) / company.total_licenses) * 100)}%` 
                                      }}
                                    />
                                  </div>
                                </div>
                              </>
                            )}
                            
                            {/* Created At */}
                            {company.created_at && (
                              <div className="px-4 py-2">
                                <div className="text-xs text-gray-500 dark:text-gray-400">{t('menu.created')}</div>
                                <div className="text-sm text-gray-900 dark:text-gray-100">
                                  {(() => {
                                    const date = new Date(company.created_at);
                                    const year = date.getFullYear();
                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                    const day = String(date.getDate()).padStart(2, '0');
                                    const hours = String(date.getHours()).padStart(2, '0');
                                    const minutes = String(date.getMinutes()).padStart(2, '0');
                                    const seconds = String(date.getSeconds()).padStart(2, '0');
                                    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default Companies;